import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormat } from '@utils/formatters';
import { Descriptions, Drawer, Input, Grid, Select } from 'antd';
import ActionsQueue from '@pages/Operational/ActionsQueue';
import { IObject } from '@utils/defaultTypes';
import moment from 'moment';
import ActionTypes from '@utils/deviceActions';
import './styles.less';

const { Search } = Input;
const { useBreakpoint } = Grid;

export interface ServiceProps {
  action: ActionTypes;
  createdBy: string;
  createdAt: string;
  status: string;
  protocol: string;
  crmBatchProtocol: string;
  operationType?: string;
  processData?: string;
}

interface IPreviewProps {
  service: ServiceProps | undefined;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AutoServicesDetails = ({ service, isOpen, setIsOpen }: IPreviewProps) => {
  const { t } = useTranslation([
    'auto-services',
    'actions-queue',
    'device-actions',
  ]);
  const screens = useBreakpoint();
  const [page, setPage] = useState<string | number | undefined>(0);
  const [linesPerPage, setLinesPerPage] = useState<string | number | undefined>(
    20,
  );
  const [filters, setFilters] = useState<IObject>({
    contract: undefined,
    msisdn: undefined,
    iccid: undefined,
    imsi: undefined,
    customerId: undefined,
  });

  const [filtersValues, setFiltersValues] = useState<IObject>({
    contractValue: undefined,
    msisdnValue: undefined,
    iccidValue: undefined,
    imsiValue: undefined,
  });

  const filtersDates = useMemo(
    () =>
      service
        ? {
            startDate: moment(service?.createdAt)?.format('YYYYMMDD'),
            endDate: moment()?.format('YYYYMMDD'),
          }
        : {},
    [service],
  );

  const actionKey = useMemo(() => Number(service?.action), [service?.action]);

  const formattedProcessData = useMemo(() => {
    if (!service?.processData) return undefined;
    if (
      [
        ActionTypes.CONTRACT_SUSPEND,
        ActionTypes.PROVISIONAL_WITHDRAW,
        ActionTypes.CONTRACT_MOVEMENT,
        ActionTypes.CANCELLATION,
      ].includes(actionKey)
    ) {
      try {
        return JSON.parse(service?.processData);
      } catch {
        return undefined;
      }
    }
    return undefined;
  }, [actionKey, service?.processData]);

  const schedulingValue = useMemo(() => {
    if (
      actionKey === ActionTypes.CONTRACT_SUSPEND &&
      formattedProcessData?.period
    ) {
      return t('days', { count: Number(formattedProcessData?.period) });
    }

    return formattedProcessData?.date;
  }, [actionKey, formattedProcessData?.date, formattedProcessData?.period, t]);

  const descriptionsItems = useMemo(
    () => [
      {
        label: t('table.operationProtocol'),
        value: service?.crmBatchProtocol,
        visible: !!service?.crmBatchProtocol,
      },
      {
        label: t('table.status'),
        value: t(`status.${service?.status}`),
        visible: !!service?.status,
      },
      {
        label: t('table.createdAt'),
        value: service?.createdAt ? dateFormat(service?.createdAt) : '-',
        visible: !!service?.createdAt,
      },
      {
        label: t('table.createdBy'),
        value: service?.createdBy,
        visible: !!service?.createdBy,
      },
      {
        label: t('table.scheduling'),
        value: schedulingValue,
        visible:
          service &&
          [
            ActionTypes.CONTRACT_SUSPEND,
            ActionTypes.PROVISIONAL_WITHDRAW,
          ].includes(Number(service?.action)),
      },
      {
        label: t('actions-queue:contract'),
        value: formattedProcessData?.contractId,
        visible: !!formattedProcessData?.contractId,
      },
      {
        label: t('actions-queue:newContract'),
        value: formattedProcessData?.newContractId,
        visible:
          actionKey === ActionTypes.CONTRACT_MOVEMENT &&
          !!formattedProcessData?.newContractId,
      },
    ],
    [t, service, schedulingValue, formattedProcessData, actionKey],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('actions-queue:msisdn'),
        key: 'msisdn',
        element: (
          <Search
            onSearch={value => {
              setFilters(oldFilters => ({ ...oldFilters, msisdn: value }));
            }}
            onChange={({ target: { value } }) => {
              setFiltersValues(oldFiltersValues => ({
                ...oldFiltersValues,
                msisdnValue: value,
              }));
            }}
            value={filtersValues?.msisdnValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('actions-queue:status'),
        key: 'status',
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                value: 'PROCESSING',
                label: t('auto-services:status.PROCESSING'),
              },
              {
                value: 'SUCCESS',
                label: t('auto-services:sim-status.SUCCESS'),
              },
              {
                value: 'ERROR',
                label: t('auto-services:status.ALL_ERROR'),
              },
            ]}
            style={{ width: '100%' }}
            value={filtersValues?.statusValue}
            onChange={value => {
              setFiltersValues(oldFiltersValues => ({
                ...oldFiltersValues,
                statusValue: value,
              }));

              setFilters(oldFilters => ({
                ...oldFilters,
                statusAutoServices: value,
              }));
            }}
            allowClear
          />
        ),
      },
    ],
    [filtersValues?.msisdnValue, filtersValues?.statusValue, t],
  );

  return (
    <Drawer
      width={screens?.xs ? '90%' : '60%'}
      placement="right"
      closable={false}
      bodyStyle={{ backgroundColor: '#f0f0f0' }}
      onClose={() => {
        setPage(0);
        setIsOpen(false);
        setLinesPerPage(20);
        setFilters({
          contract: undefined,
          msisdn: undefined,
          iccid: undefined,
          imsi: undefined,
          customerId: undefined,
          statusAutoServices: undefined,
        });
        setFiltersValues({
          contractValue: undefined,
          msisdnValue: undefined,
          iccidValue: undefined,
          imsiValue: undefined,
          statusValue: undefined,
        });
      }}
      destroyOnClose
      open={isOpen}
    >
      <div className="drawer-auto-services-content">
        <Descriptions column={{ lg: 2, sm: 1, xs: 1 }} style={{ padding: 24 }}>
          {descriptionsItems?.map(
            ({ label, value, visible }) =>
              value &&
              visible && (
                <Descriptions.Item key={label} label={label}>
                  {value}
                </Descriptions.Item>
              ),
          )}
        </Descriptions>
        <ActionsQueue
          tableOnly
          defaultFilters={tableFilters}
          operationType={service?.operationType || String(service?.action)}
          defaultFiltersValues={{
            page: Number(page) > 0 ? Number(page) - 1 : 0,
            linesPerPage,
            operationProtocol: service?.protocol,
            ...filters,
            ...filtersDates,
          }}
          defaultColumns={[
            'msisdn',
            'iccid',
            'imsi',
            'newIccid',
            'flagProcessed',
            'childProtocol',
          ]}
          defaultPagination={{ page, linesPerPage }}
          tableOnlyKey="serviceDetails"
          setDefaultPaginationChange={values => {
            setPage(values?.page);
            setLinesPerPage(values?.linesPerPage);
          }}
        />
      </div>
    </Drawer>
  );
};

export default AutoServicesDetails;
